import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Button, Card, Form } from "react-bootstrap";
import { faNotesMedical, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Swal from "sweetalert2";

import "./style.scss";
import { ClinicApi } from "../../api";
import {
    ClinicInsightDashboardType,
    ClinicPatientType,
    PaginationType,
} from "../../DTO";
import {
    CustomBreadCrumb,
    CustomPagination,
    Loading,
} from "../../components";
import {
    useAppLocation,
    useAppNavigate,
    useAppPagination,
} from "../../Router/hook";
import { useAppSelector } from "../../redux-config/hooks";
import { selectUser } from "../../redux-config/entities/user";
import { getClinicDisplayName } from "../../utils";

const conditionOptions = ["CM Risk", "T2D", "Prediabetes"];
const therapyOptions = ["GLP-1", "Metformin", "Lifestyle", "insulin", "oral medication"];
const sortOptions = [
    "Risk (High → Low)",
    "Risk (Low → High)",
    "New patients (Latest)",
    "Old patients (Earliest)",
];

const getMonthlyData = (patient: ClinicPatientType) => {
    if (!hasJoinedApp(patient)) {
        return null;
    }

    return (
        patient.progressOverview?.latestMonthly ||
        patient.latestMonthlySummary ||
        patient.raw?.progress_overview?.latest_monthly ||
        patient.raw?.latest_monthly_summary ||
        null
    );
};

const hasJoinedApp = (patient: ClinicPatientType) => {
    return patient.isJoined && Boolean(patient.userId);
};

const getTrendDirection = (patient: ClinicPatientType) => {
    const monthly = getMonthlyData(patient);

    return monthly?.trendDirection || monthly?.trend?.direction || null;
};

const getLifestyleCounts = (patient: ClinicPatientType) => {
    const monthly = getMonthlyData(patient);

    return {
        green:
            monthly?.greenDaysCount ??
            monthly?.traffic_light_distribution?.green_days ??
            0,
        amber:
            monthly?.amberDaysCount ??
            monthly?.traffic_light_distribution?.amber_days ??
            0,
        red:
            monthly?.redDaysCount ??
            monthly?.traffic_light_distribution?.red_days ??
            0,
        days:
            monthly?.daysWithDataCount ??
            monthly?.traffic_light_distribution?.days_with_data ??
            monthly?.days_with_data_count ??
            0,
    };
};

const getDriverLight = (driver: any) => {
    const light = driver?.light;

    if (light === "green" || light === "amber" || light === "yellow" || light === "red") {
        return light === "yellow" ? "amber" : light;
    }

    const numeric = Number(driver?.avgScore ?? driver?.avg_score);

    if (!Number.isFinite(numeric)) {
        return "amber";
    }

    if (numeric >= 75) {
        return "green";
    }

    if (numeric >= 60) {
        return "amber";
    }

    return "red";
};

const getDriverMeta = (monthly: any, key: string, fallbackKey?: string) => {
    return (
        monthly?.categoryAverages?.[key] ||
        monthly?.category_averages?.[key] ||
        (fallbackKey
            ? monthly?.categoryAverages?.[fallbackKey] ||
              monthly?.category_averages?.[fallbackKey]
            : null)
    );
};

const getPatientDetailDriverLights = (patient: ClinicPatientType) => {
    const monthly = getMonthlyData(patient);

    return [
        getDriverMeta(monthly, "nutrition", "carbs"),
        getDriverMeta(monthly, "meal_pattern", "sugar"),
        getDriverMeta(monthly, "activity"),
        getDriverMeta(monthly, "sleep"),
        getDriverMeta(monthly, "mindset", "stress"),
    ].map(getDriverLight);
};

const getRiskMeta = (patient: ClinicPatientType) => {
    if (!hasJoinedApp(patient)) {
        return {
            label: "Awaiting signup",
            action: "View",
            tone: "muted",
        };
    }

    const counts = getLifestyleCounts(patient);
    const trend = getTrendDirection(patient);

    if (patient.condition === "CM Risk" || counts.red >= 2 || trend === "declining") {
        return {
            label: "High Risk",
            action: "Escalate",
            tone: "danger",
        };
    }

    if (patient.condition === "T2D" || counts.amber >= 2) {
        return {
            label: "At Risk",
            action: "Monitor",
            tone: "warning",
        };
    }

    return {
        label: "Stable",
        action: "Maintain",
        tone: "success",
    };
};

const getLifestyleDriver = (patient: ClinicPatientType) => {
    if (!hasJoinedApp(patient)) {
        return "Awaiting signup";
    }

    const driverLights = getPatientDetailDriverLights(patient);
    const greenCount = driverLights.filter((light) => light === "green").length;
    const total = driverLights.length;

    if (greenCount >= 4) {
        return `Green ${greenCount}/${total} → Stable`;
    }

    if (greenCount === 3) {
        return `Amber ${greenCount}/${total} ↑ Improving`;
    }

    return `Red ${greenCount}/${total} ↓ Worsening`;
};

const getLifestyleTone = (patient: ClinicPatientType) => {
    const driver = getLifestyleDriver(patient);

    if (driver.startsWith("Red")) {
        return "danger";
    }

    if (driver.startsWith("Amber")) {
        return "warning";
    }

    if (driver.startsWith("Awaiting")) {
        return "muted";
    }

    return "success";
};

const riskRank = (patient: ClinicPatientType) => {
    const tone = getRiskMeta(patient).tone;

    if (tone === "danger") {
        return 0;
    }

    if (tone === "warning") {
        return 1;
    }

    return 2;
};

const createdAtTime = (patient: ClinicPatientType) => {
    const time = patient.createdAt ? new Date(patient.createdAt).getTime() : 0;

    return Number.isFinite(time) ? time : 0;
};

const openPatientReport = (patient: ClinicPatientType) => {
    const reportUrl = patient.report?.url || patient.report?.downloadUrl;

    if (!reportUrl) {
        Swal.fire({
            icon: "info",
            text: "No static report is available for this patient.",
        });
        return;
    }

    window.open(reportUrl, "_blank", "noopener,noreferrer");
};

const clinicalSummaryValue = (patient: ClinicPatientType, label: string) => {
    const row = patient.clinicalReportSummary?.clinicalSummary?.find(
        (item) => item.label.toLowerCase() === label.toLowerCase(),
    );

    return row?.value ?? "";
};

const getClinicalInsights = (patient: ClinicPatientType) => {
    const weight = clinicalSummaryValue(patient, "Weight");
    const fbg = clinicalSummaryValue(patient, "FBG");
    const bp = clinicalSummaryValue(patient, "BP");

    if (fbg.includes("↑") && bp.includes("↑")) {
        return "FBG↑ BP↑";
    }

    if (weight.includes("↓") && fbg.includes("↓")) {
        return "Wt↓ FBG↓";
    }

    if (!hasJoinedApp(patient)) {
        return "Awaiting signup";
    }

    return "Stable";
};

const getReportingPeriod = () => {
    const formatter = new Intl.DateTimeFormat("en", {
        month: "short",
        year: "numeric",
    });

    return formatter.format(new Date());
};

const getDashboardClinicName = (patients: ClinicPatientType[], user: any) => {
    return (
        patients.find((patient) => patient.clinic?.name)?.clinic?.name ||
        user?.clinic?.name ||
        user?.clinicName ||
        user?.clinic_name ||
        "Kelowna PCN"
    );
};

export const ClinicPatientsList = () => {
    const user = useAppSelector(selectUser);
    const navigate = useAppNavigate();
    const location = useAppLocation();

    const [loading, setLoading] = useState(false);
    const [patients, setPatients] = useState<ClinicPatientType[]>([]);
    const [pagination, setPagination] =
        useState<PaginationType>(useAppPagination());
    const [condition, setCondition] = useState("");
    const [therapy, setTherapy] = useState("");
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState(sortOptions[0]);
    const [insights, setInsights] = useState<ClinicInsightDashboardType>();

    const view = new URLSearchParams(location.search).get("view") || "dashboard";
    const isInsightsView = view === "insights";

    const crumbs = [
        { name: "", href: "" },
        { name: "Clinic", href: "/clinic/patients" },
        { name: "Dashboard", href: "#" },
    ];

    const fetchPatients = useCallback(
        async (
            page: number,
            filters = {
                condition,
                therapy,
            },
        ) => {
            try {
                setLoading(true);
                const data = await ClinicApi.FetchClinicPatientList(user, page, {
                    condition: filters.condition,
                    therapy: filters.therapy,
                    signupStatus: "all",
                });
                setPatients(data.patients);
                setPagination(data.pagination);
            } catch (err: any) {
                Swal.fire({
                    icon: "error",
                    text:
                        err?.data?.message ||
                        err?.message ||
                        "Unable to load clinic patients.",
                });
            } finally {
                setLoading(false);
            }
        },
        [condition, therapy, user],
    );

    const fetchInsights = useCallback(
        async () => {
            try {
                setLoading(true);
                const data = await ClinicApi.FetchClinicInsights(user);
                setInsights(data.insights);
            } catch (err: any) {
                Swal.fire({
                    icon: "error",
                    text:
                        err?.data?.message ||
                        err?.message ||
                        "Unable to load clinic insights.",
                });
            } finally {
                setLoading(false);
            }
        },
        [user],
    );

    useEffect(() => {
        if (isInsightsView) {
            fetchInsights();
        } else {
            fetchPatients(pagination.current_page);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInsightsView]);

    useEffect(() => {
        const state = location.state as { successMessage?: string } | null;

        if (state?.successMessage) {
            Swal.fire({
                icon: "success",
                title: "Referral created successfully",
                text: state.successMessage,
            });
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.pathname, location.state, navigate]);

    const visiblePatients = useMemo(() => {
        const term = search.trim().toLowerCase();

        return patients.filter((patient) => {
            if (!term) {
                return true;
            }

            return [
                getClinicDisplayName(patient),
                patient.email,
                patient.phone,
                patient.identityCode,
                patient.insuranceMemberId,
            ]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(term));
        }).sort((a, b) => {
            if (sort === "New patients (Latest)") {
                return createdAtTime(b) - createdAtTime(a);
            }

            if (sort === "Old patients (Earliest)") {
                return createdAtTime(a) - createdAtTime(b);
            }

            if (sort === "Risk (Low → High)") {
                return riskRank(b) - riskRank(a);
            }

            return riskRank(a) - riskRank(b);
        });
    }, [patients, search, sort]);

    const stats = useMemo(() => {
        const highRisk = patients.filter(
            (patient) => getRiskMeta(patient).tone === "danger",
        ).length;
        const improving = patients.filter((patient) =>
            getLifestyleDriver(patient).includes("Improving"),
        ).length;
        const newReferrals = patients.filter((patient) => {
            if (!patient.createdAt) {
                return false;
            }

            const createdAt = new Date(patient.createdAt).getTime();
            const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;

            return createdAt >= thirtyDaysAgo;
        }).length;

        return {
            total: patients.length,
            highRisk,
            improving,
            newReferrals,
            referrals: pagination.total || patients.length,
        };
    }, [pagination.total, patients]);

    const resetFilters = () => {
        setCondition("");
        setTherapy("");
        setSearch("");
        setSort(sortOptions[0]);
        if (isInsightsView) {
            fetchInsights();
            return;
        }

        fetchPatients(1, { condition: "", therapy: "" });
    };

    if (isInsightsView) {
        return (
            <div className="my-4 clinic-page clinic-insights-page">
                <Helmet>
                    <title>D2Type | Clinic Insights</title>
                </Helmet>

                <CustomBreadCrumb breadCrumbList={crumbs} />

                <div className="clinic-shell">
                    <div className="clinic-snapshot-header">
                        <div>
                            <h2 className="clinic-snapshot-title">Clinic Insights</h2>
                            <p className="clinic-snapshot-subtitle">
                                Population-level drivers for targeted action
                            </p>
                        </div>
                        <div className="clinic-toolbar-actions">
                            <Button
                                variant="outline-primary"
                                onClick={() => fetchInsights()}
                            >
                                Live view
                            </Button>
                        </div>
                    </div>

                    {loading ? (
                        <Loading />
                    ) : (
                        <div className="clinic-insight-card-grid">
                            {(insights?.cards ?? []).map((card) => (
                                <section className="clinic-insight-card" key={card.key}>
                                    <h3>{card.title}</h3>
                                    <ul>
                                        {card.items.map((item) => (
                                            <li key={item}>{item}</li>
                                        ))}
                                    </ul>
                                </section>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="my-4 clinic-page clinic-snapshot-page">
            <Helmet>
                <title>D2Type | Clinic Monthly Snapshot</title>
            </Helmet>

            <CustomBreadCrumb breadCrumbList={crumbs} />

            <div className="clinic-shell">
                <div className="clinic-snapshot-header">
                    <div>
                        <h2 className="clinic-snapshot-title">Clinic Monthly Snapshot</h2>
                        <p className="clinic-snapshot-subtitle">
                            Reporting period: {getReportingPeriod()} | Patients
                            prioritized by clinical risk and behavioral drivers
                        </p>
                    </div>
                    <div className="clinic-toolbar-actions">
                        <div className="clinic-detail-clinic-name" aria-disabled="true">
                            {getDashboardClinicName(patients, user)}
                        </div>
                        <Button
                            variant="outline-primary"
                            onClick={() => fetchPatients(pagination.current_page)}
                        >
                            Live view
                        </Button>
                        <Button
                            onClick={() => navigate("/clinic/patients/new")}
                            variant="danger"
                            className="px-4"
                        >
                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                            New Referral
                        </Button>
                    </div>
                </div>

                <Card className="clinic-surface clinic-filter-surface border-0">
                    <Card.Body className="clinic-panel">
                        <div className="clinic-snapshot-filter-grid">
                            <Form.Select
                                value={condition}
                                onChange={(event) => setCondition(event.target.value)}
                            >
                                <option value="">Condition</option>
                                {conditionOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Select
                                value={therapy}
                                onChange={(event) => setTherapy(event.target.value)}
                            >
                                <option value="">Therapy</option>
                                {therapyOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Select
                                value={sort}
                                onChange={(event) => setSort(event.target.value)}
                            >
                                {sortOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Control
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Search patient or MRN"
                            />
                            <Button
                                variant="primary"
                                onClick={() => fetchPatients(1)}
                            >
                                Apply
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={resetFilters}
                            >
                                Reset
                            </Button>
                        </div>
                    </Card.Body>
                </Card>

                <div className="clinic-snapshot-stat-grid">
                    <div className="clinic-snapshot-stat-card">
                        <span>Total patients</span>
                        <strong>{stats.total}</strong>
                        <small>active</small>
                    </div>
                    <div className="clinic-snapshot-stat-card">
                        <span>High risk</span>
                        <strong className="clinic-text-danger">{stats.highRisk}</strong>
                        <small>need review</small>
                    </div>
                    <div className="clinic-snapshot-stat-card">
                        <span>Improving</span>
                        <strong className="clinic-text-success">{stats.improving}</strong>
                        <small>this month</small>
                    </div>
                    <div className="clinic-snapshot-stat-card">
                        <span>New referrals</span>
                        <strong className="clinic-text-primary">{stats.newReferrals}</strong>
                        <small>30 days</small>
                    </div>
                    <div className="clinic-snapshot-stat-card">
                        <span>Total referrals</span>
                        <strong>{stats.referrals}</strong>
                    </div>
                </div>

                {loading ? (
                    <Loading />
                ) : visiblePatients.length === 0 ? (
                    <Card className="clinic-surface border-0">
                        <Card.Body className="clinic-empty-state">
                            <div className="clinic-empty-icon mx-auto mb-3">
                                <FontAwesomeIcon icon={faNotesMedical} size="2x" />
                            </div>
                            <h3 className="h4 mb-2">No patient referrals found</h3>
                            <p className="text-muted mb-4">
                                Adjust the filters or create a new referral to start
                                tracking the monthly clinic queue.
                            </p>
                            <Button
                                variant="danger"
                                onClick={() => navigate("/clinic/patients/new")}
                            >
                                New Referral
                            </Button>
                        </Card.Body>
                    </Card>
                ) : (
                    <div className="clinic-snapshot-table-wrap">
                        <table className="clinic-snapshot-table">
                            <thead>
                                <tr>
                                    <th>Patient</th>
                                    <th>Condition</th>
                                    <th>Risk</th>
                                    <th>Therapy</th>
                                    <th>Lifestyle Drivers</th>
                                    <th>Clinical Insights</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visiblePatients.map((patient) => {
                                    const risk = getRiskMeta(patient);
                                    const lifestyleTone = getLifestyleTone(patient);

                                    return (
                                        <tr
                                            key={patient.id}
                                            className={
                                                risk.tone === "danger"
                                                    ? "clinic-snapshot-row-danger"
                                                    : ""
                                            }
                                        >
                                            <td>
                                                <button
                                                    className="clinic-link-button"
                                                    type="button"
                                                    onClick={() =>
                                                        navigate(
                                                            `/clinic/patients/${patient.id}`,
                                                        )
                                                    }
                                                >
                                                    {getClinicDisplayName(patient)}
                                                </button>
                                            </td>
                                            <td>{patient.condition || "-"}</td>
                                            <td>
                                                <span className={`clinic-risk-text clinic-risk-${risk.tone}`}>
                                                    {risk.label}
                                                </span>
                                            </td>
                                            <td>{patient.therapy || "-"}</td>
                                            <td>
                                                <span className={`clinic-risk-text clinic-risk-${lifestyleTone}`}>
                                                    {getLifestyleDriver(patient)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="clinic-insight-text">
                                                    {getClinicalInsights(patient)}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="clinic-row-actions">
                                                    <Button
                                                        size="sm"
                                                        variant={
                                                            risk.tone === "danger"
                                                                ? "outline-danger"
                                                                : risk.tone === "warning"
                                                                  ? "outline-warning"
                                                                  : "outline-primary"
                                                        }
                                                        onClick={() =>
                                                            navigate(
                                                                `/clinic/patients/${patient.id}`,
                                                            )
                                                        }
                                                    >
                                                        {risk.action}
                                                    </Button>
                                                    {!hasJoinedApp(patient) && (
                                                        <Button
                                                            size="sm"
                                                            variant={
                                                                patient.onboardingStatus === "invitation"
                                                                    ? "outline-secondary"
                                                                    : "outline-success"
                                                            }
                                                            title={
                                                                patient.onboardingStatus === "invitation"
                                                                    ? "Download link already sent — click to resend"
                                                                    : "Send download link to patient"
                                                            }
                                                            onClick={() =>
                                                                ClinicApi.SendClinicPatientInvitation(
                                                                    user,
                                                                    patient.id,
                                                                )
                                                                    .then(() => {
                                                                        Swal.fire({
                                                                            icon: "success",
                                                                            text: "Download link sent successfully.",
                                                                            timer: 2000,
                                                                            showConfirmButton: false,
                                                                        });
                                                                        fetchPatients(pagination.current_page);
                                                                    })
                                                                    .catch(() =>
                                                                        Swal.fire({
                                                                            icon: "error",
                                                                            text: "Failed to send download link.",
                                                                        }),
                                                                    )
                                                            }
                                                        >
                                                            {patient.onboardingStatus === "invitation"
                                                                ? "Resend Link"
                                                                : "Send Download Link"}
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="outline-secondary"
                                                        onClick={() => openPatientReport(patient)}
                                                    >
                                                        Report
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {pagination.total > pagination.per_page && (
                    <Card className="clinic-surface border-0">
                        <Card.Body className="clinic-panel">
                            <CustomPagination
                                total={pagination.total}
                                perPage={pagination.per_page}
                                currentPage={pagination.current_page}
                                changePage={(page: number) => {
                                    navigate(`/clinic/patients?current_page=${page}`);
                                    fetchPatients(page);
                                }}
                            />
                        </Card.Body>
                    </Card>
                )}

                <div className="clinic-snapshot-legend">
                    <p>
                        Legend: Red indicates worsening, Amber indicates monitor,
                        Green indicates stable or improving. Risk reflects clinical
                        condition and monthly behavioral trend.
                    </p>
                    <p>
                        Lifestyle score reflects engagement across nutrition,
                        activity, sleep, stress, and adherence. Action items indicate
                        recommended next clinical steps.
                    </p>
                </div>
            </div>
        </div>
    );
};
